import { useCallback, useMemo, type ReactNode } from "react"
import { useRouter, useRouterState } from "@tanstack/react-router"

import { ViewStateScopeProvider, ViewStateUrlProvider } from "./component-view-state"
import { normalizeUrlViewStateValues } from "./view-state-url"

type ViewStateNavigate = (options: {
  replace: true
  state: true
  search: (previous: Record<string, unknown>) => Record<string, unknown>
}) => void | Promise<unknown>

export function ViewStateRouterProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const location = useRouterState({ select: (state) => state.location })
  const navigateViewState = router.navigate as unknown as ViewStateNavigate
  const routeScope = location.pathname.replace(/\/$/, "") || "/"
  const values = useMemo(
    () => normalizeUrlViewStateValues(location.search as Readonly<Record<string, unknown>>),
    [location.search]
  )
  const onValueChange = useCallback(
    (id: string, value: string | undefined) => {
      void navigateViewState({
        replace: true,
        state: true,
        search: (previous) => {
          const next = { ...previous }
          if (value === undefined) delete next[id]
          else next[id] = value
          return next
        },
      })
    },
    [navigateViewState]
  )

  return (
    <ViewStateUrlProvider values={values} onValueChange={onValueChange}>
      <ViewStateScopeProvider id={routeScope}>{children}</ViewStateScopeProvider>
    </ViewStateUrlProvider>
  )
}
